import React, { useEffect, useRef, useState } from 'react'

import * as S from './styles'
import { TextHomeContainer } from '../textHomeContainer'
import { NhShape } from '@/assets/imgs'
import { Natom } from '@/components'
import { HomePage } from '@/service/api/home'

type NhShapeContainerProps = {
  data: HomePage['textHome']
}
type PointScale = {
  x?: number
  y?: number
  scale: number
  minScale: number
  maxScale: number
}

export const NhShapeContainer: React.FC<NhShapeContainerProps> = ({
  data,
}: NhShapeContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const natomRef = useRef<HTMLDivElement>(null)
  const pointRef = useRef<PointScale>({
    x: 0,
    y: 0,
    scale: 1,
    minScale: 1,
    maxScale: 0,
  })
  const natomPointRef = useRef<PointScale>({
    scale: 1,
    minScale: 0.001,
    maxScale: 1,
  })
  const onWhitePageRef = useRef(false)
  const isScrollbarInteracted = useRef(false)
  const previousScrollY = useRef(0)
  const [isVisible, setIsVisible] = useState(false)

  const [activeScroll, setActiveScroll] = useState(false)

  const defaultOffset = -38

  const blockedScroll = useRef(false)

  useEffect(() => {
    const container = containerRef.current
    const natom = natomRef.current
    const img = imgRef.current
    const point = pointRef.current
    const natomPoint = natomPointRef.current

    if (!container || !img || !natom) return

    window.addEventListener('mouseup', () => {
      isScrollbarInteracted.current = false
    })

    const handleContainerScroll = (
      deltaY: number,
      event?: WheelEvent | TouchEvent | Event,
    ) => {
      if (blockedScroll.current) {
        event?.preventDefault()
        return
      }

      const aproxLogoPillarSize = img.width / 10
      const scaleDiff = window.innerWidth / aproxLogoPillarSize
      point.maxScale = scaleDiff * 1.2 // safety offset

      if (natom.classList.contains('visible')) {
        onWhitePageRef.current = true
      } else {
        onWhitePageRef.current = false
      }

      const blockScroll = () => {
        document.body.style.position = 'fixed'
        document.body.style.top = `-${container.getBoundingClientRect().top}px`
        document.body.style.left = '0'
        document.body.style.right = '0'
        blockedScroll.current = true
      }

      const freeScroll = () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.left = ''
        document.body.style.right = ''
        container.scrollIntoView({ behavior: 'instant' as ScrollBehavior })

        previousScrollY.current = window.scrollY

        blockedScroll.current = false
      }

      const getTranslate = () => {
        // amount in pixels to be translated if the image had its initial size
        const defaultSizeTranslateX = 46.2 // px
        const defaultSizeTranslateY = -55 // px

        const translateX = point.maxScale * defaultSizeTranslateX
        const translateY = point.maxScale * defaultSizeTranslateY

        return `${translateX},${translateY}`
      }

      // using a lower value or zero might result in flickering
      const defaultRotate = '0.0001,0.0001'
      const defaultTranslate = '0.0001,0.0001'

      if (!onWhitePageRef.current) {
        if (deltaY > 0) {
          blockScroll()
          const translate = getTranslate()
          const nhShapeScaleUpMatrix = `matrix(${point.maxScale},${defaultRotate},${point.maxScale},${translate})`
          img.style.transform = nhShapeScaleUpMatrix

          natom.classList.add('visible', 'scaleup')
          const natomScaleUpMatrix = `matrix(${natomPoint.maxScale},${defaultRotate},${natomPoint.maxScale},${defaultTranslate})`
          natom.style.transform = natomScaleUpMatrix

          setTimeout(() => {
            setIsVisible(true)

            setTimeout(() => {
              natom.classList.remove('scaleup')

              // updating values
              point.scale = point.maxScale
              natomPoint.scale = natomPoint.maxScale
              freeScroll()
            }, 500)
          }, 1000)
        }
      } else if (onWhitePageRef.current && deltaY < 0) {
        blockScroll()

        const natomScaleDownMatrix = `matrix(${natomPoint.minScale},${defaultRotate},${natomPoint.minScale},${defaultTranslate})`
        natom.style.transform = natomScaleDownMatrix

        setTimeout(() => {
          natom.classList.remove('visible')
          setIsVisible(false)
          const nhShapeScaleDownMatrix = `matrix(${point.minScale},${defaultRotate},${point.minScale},${defaultTranslate})`
          img.style.transform = nhShapeScaleDownMatrix
          setTimeout(() => {
            // updating values
            point.scale = point.minScale
            natomPoint.scale = natomPoint.minScale
            freeScroll()
          }, 1000)
        }, 1000)
      }
    }

    const handleWindowScroll = (event: Event) => {
      const { scrollY } = window

      const isOnContainerViewPort =
        scrollY >= container.offsetTop - 50 &&
        scrollY <= container.offsetTop + 100

      if (blockedScroll.current) {
        event.preventDefault()
        container.scrollIntoView({ behavior: 'instant' as ScrollBehavior })
        return
      }

      if (isOnContainerViewPort) {
        setActiveScroll(true)

        const deltaY = scrollY - previousScrollY.current
        previousScrollY.current = scrollY
        handleContainerScroll(deltaY, event)
      }
      setActiveScroll(false)

      const isOnTopWithoutEndAnimation =
        scrollY < container.offsetTop && point.scale !== 1
      if (isOnTopWithoutEndAnimation) {
        container.scrollIntoView({ behavior: 'instant' as ScrollBehavior })
        return
      }

      const isAtBottomWithoutEndAnimation =
        scrollY > container.offsetTop && point.scale !== point.maxScale
      if (isAtBottomWithoutEndAnimation) {
        container.scrollIntoView({ behavior: 'instant' as ScrollBehavior })
      }
    }

    document.addEventListener('scroll', handleWindowScroll, {
      passive: false,
    })
    document.addEventListener('wheel', handleWindowScroll, {
      passive: false,
    })
    document.addEventListener('touchmove', handleWindowScroll, {
      passive: false,
    })

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('scroll', handleWindowScroll)
      document.removeEventListener('wheel', handleWindowScroll)
      document.removeEventListener('touchmove', handleWindowScroll)
    }
  }, [])

  return (
    <S.FlexContainerNhShape
      isVisible={isVisible}
      style={{
        pointerEvents: activeScroll ? 'auto' : 'none',
      }}
      ref={containerRef}
    >
      <S.NatomContainer ref={natomRef}>
        <Natom />
      </S.NatomContainer>
      <S.ImageWrapper isVisible={isVisible}>
        <S.NhImage
          offset={defaultOffset}
          ref={imgRef}
          src={NhShape}
          alt="Imagem que representa a nata house"
          width={0}
          height={0}
        />
        <div className="icon-scroll" />
      </S.ImageWrapper>
      <TextHomeContainer data={data} isVisible={isVisible} />
    </S.FlexContainerNhShape>
  )
}
